import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { formatClock } from '../../constants';

export interface SectionEndStats {
  answered: number;
  unanswered: number;
  /** Seconds spent in the section; null when it was untimed. */
  spentSeconds: number | null;
}

interface Props {
  testTitle: string;
  finished: { title: string; stats: SectionEndStats | null };
  /** Every section in order, with whether the candidate is past it. */
  timeline: Array<{ id: string; title: string; done: boolean }>;
  next: { title: string; minutes: number | null; questions: number | null } | null;
  /** Seconds until the server starts the next section by itself. */
  remaining: number | null;
  busy: boolean;
  onStart: () => void;
}

/** Between two IELTS sections: what just closed, and the next one waiting for the candidate or the server's countdown. */
export function SectionEndScreen({ testTitle, finished, timeline, next, remaining, busy, onStart }: Props) {
  const { t } = useTranslation();
  const nextIndex = timeline.findIndex((s) => !s.done);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[#F4F6FB] text-[#10182B]">
      <header className="flex h-14 shrink-0 items-center gap-3 bg-[#0A1628] px-4 text-white sm:h-16 sm:px-8">
        <span
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-[#D1FE17] font-display text-base font-extrabold text-[#10182B]"
          aria-hidden
        >
          E
        </span>
        <b className="truncate text-sm">{testTitle}</b>
      </header>

      <main className="flex flex-1 items-start justify-center p-4 sm:items-center sm:p-8">
        <section
          aria-labelledby="section-end-title"
          className="w-full max-w-[820px] rounded-[22px] border border-[#E6E9F2] bg-white p-5 shadow-[0_30px_60px_-40px_rgba(10,22,40,0.35)] sm:rounded-[28px] sm:px-11 sm:py-10"
        >
          <span className="font-mono text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#166534]">{t('exam.section_end.eyebrow')}</span>
          <h1 id="section-end-title" className="mt-2.5 font-display text-[28px] font-extrabold leading-[1.1] tracking-[-0.045em] sm:text-[38px]">
            {t('exam.section_end.title', { section: finished.title })}
          </h1>

          <ol aria-label={t('exam.section_end.steps')} className="mt-7 flex">
            {timeline.map((s, i) => {
              const isNext = i === nextIndex;
              return (
                <li
                  key={s.id}
                  aria-current={isNext ? 'step' : undefined}
                  className={cn(
                    'relative flex-1 text-center text-[13px] font-bold sm:text-[13.5px]',
                    s.done ? 'text-[#166534]' : isNext ? 'text-[#10182B]' : 'text-[#8A93A8]',
                  )}
                >
                  {i > 0 && (
                    <span
                      aria-hidden
                      className={cn('absolute left-[-50%] right-1/2 top-[15px] h-[3px]', s.done ? 'bg-[#86C9A0]' : isNext ? 'bg-[#0A1628]' : 'bg-[#E6E9F2]')}
                    />
                  )}
                  <i
                    aria-hidden
                    className={cn(
                      'relative z-[1] mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full border-2 text-[13px] not-italic',
                      s.done
                        ? 'border-[#86C9A0] bg-[#E6F4EC]'
                        : isNext
                          ? 'border-[#0A1628] bg-[#0A1628] text-white'
                          : 'border-[#CBD2E3] bg-white',
                    )}
                  >
                    {s.done ? '✓' : i + 1}
                  </i>
                  {s.title}
                </li>
              );
            })}
          </ol>

          {finished.stats && (
            <dl className="mt-7 grid grid-cols-3 gap-2.5 sm:gap-3">
              <Stat value={String(finished.stats.answered)} label={t('exam.section_end.answered')} />
              <Stat value={String(finished.stats.unanswered)} label={t('exam.section_end.unanswered')} />
              {finished.stats.spentSeconds !== null && <Stat value={formatClock(finished.stats.spentSeconds)} label={t('exam.section_end.spent')} />}
            </dl>
          )}

          <p className="mt-[18px] rounded-xl bg-[#FFF4D6] px-4 py-3 text-[13.5px] font-semibold text-[#92400E]">
            {t('exam.section_end.locked', { section: finished.title })}
          </p>

          {next && (
            <div className="mt-6 flex flex-col gap-4 rounded-[20px] bg-[#0A1628] p-5 text-white sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:px-[22px]">
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-slate-400">{t('exam.section_end.next')}</div>
                <div className="mt-0.5 font-display text-xl font-extrabold tracking-[-0.03em] sm:text-2xl">
                  {next.title}
                  {next.minutes ? ` · ${t('exam.section_end.minutes', { count: next.minutes })}` : ''}
                  {next.questions ? ` · ${t('exam.section_end.questions', { count: next.questions })}` : ''}
                </div>
                {remaining !== null && (
                  <div className="mt-1.5 text-[13px] text-[#C9CCE6]">
                    {t('exam.section_end.auto_start')}{' '}
                    <b role="timer" aria-label={formatClock(remaining)} className="font-mono">
                      {formatClock(remaining)}
                    </b>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onStart}
                disabled={busy}
                className="inline-flex min-h-[52px] shrink-0 items-center justify-center rounded-[14px] bg-[#D1FE17] px-6 text-[15px] font-bold text-[#10182B] transition hover:brightness-95 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-[#D1FE17] disabled:opacity-60"
              >
                {t('exam.section_end.start_now', { section: next.title })}
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-[#EEF1F8] bg-[#F7F9FD] px-3 py-3.5 sm:px-[18px] sm:py-4">
      <dd className="font-display text-2xl font-extrabold tracking-[-0.04em] sm:text-[28px]">{value}</dd>
      <dt className="text-xs text-[#646D84] sm:text-[12.5px]">{label}</dt>
    </div>
  );
}

export default SectionEndScreen;
